import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box,
    Button,
    CircularProgress,
    Typography,
} from '@mui/material';
import { ArrowBack as ArrowBackIcon, Refresh as RetryIcon } from '@mui/icons-material';
import ChatHeader from '../components/chat/ChatHeader';
import CurriculumPage from '../components/admin/CurriculumPage';
import config from '../config';
import { authFetch, handleApiResponse, clearToken } from '../api/authFetch';
// This page mounts under App.js's chatGPTTheme (Söhne, ChatGPT green
// primary), not adminTheme — tutorTokens is imported explicitly (same
// pattern as StudentTutor.js) so the error card below reads with the
// product's actual indigo accent instead of silently inheriting green from
// an unrelated theme, and matches the same card/color treatment used by
// StudentTutorSession.js and StudentTutor.js for the identical failure state.
import { tutorTokens } from '../theme/tutorTheme';

const StudentCurriculum = () => {
    const navigate = useNavigate();
    const [currentUser, setCurrentUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);
    const [retryToken, setRetryToken] = useState(0);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            setLoading(true);
            setError(false);
            try {
                const res = await authFetch(`${config.API_BASE_URL}/api/me`);
                const data = await handleApiResponse(res, navigate);
                if (data === null || cancelled) return; // handleApiResponse already sent us to /signin on a real auth failure
                setCurrentUser(data);
            } catch (e) {
                if (cancelled) return;
                // handleApiResponse redirects and returns null on 401 itself
                // - it never throws for that case, so anything caught here
                // (including a non-2xx ApiError) is a real load failure, not
                // an auth problem. Let the student retry instead of
                // bouncing them out as if they'd been signed out.
                setError(true);
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [navigate, retryToken]);

    const handleLogout = async () => {
        await fetch(`${config.API_BASE_URL}/api/logout`, {
            method: 'POST',
            credentials: 'include',
        });
        clearToken();
        navigate('/signin');
    };

    // Plain navigation back to /chat - there's nothing to delete here, so
    // ChatHeader is told confirmDelete={false} below and skips the
    // delete-confirmation dialog entirely for this surface.
    const handleBackToChat = () => {
        navigate('/chat');
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        // Same card, icon-circle, indigo accent, and copy as
        // StudentTutorSession.js / StudentTutor.js's error states — one
        // consistent "page failed to load" treatment across all student
        // pages instead of three. role="alert" so it's announced the
        // moment it replaces the spinner (this is a hand-rolled Box, not
        // MUI's <Alert>, which gets that for free).
        return (
            <Box sx={{ minHeight: '100vh', width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 3, backgroundColor: tutorTokens.surface }}>
                <Box
                    role="alert"
                    sx={{
                        maxWidth: 420,
                        width: '100%',
                        textAlign: 'center',
                        borderRadius: `${tutorTokens.radiusXl}px`,
                        border: `1px solid ${tutorTokens.border}`,
                        backgroundColor: tutorTokens.surfaceRaised,
                        boxShadow: tutorTokens.shadowCard,
                        p: { xs: 3, sm: 4 },
                    }}
                >
                    <Box
                        sx={{
                            width: 56,
                            height: 56,
                            mx: 'auto',
                            mb: 2,
                            borderRadius: '50%',
                            backgroundColor: tutorTokens.border,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}
                    >
                        <RetryIcon sx={{ fontSize: 28, color: tutorTokens.inkSoft }} />
                    </Box>
                    <Typography sx={{ fontFamily: tutorTokens.fontUI, fontWeight: 600, fontSize: '1.3rem', color: tutorTokens.ink, mb: 1 }}>
                        We couldn't load this page
                    </Typography>
                    <Typography sx={{ color: tutorTokens.inkSoft, fontSize: '0.9rem', mb: 3 }}>
                        This didn't work. Try again — if it keeps happening, tell your teacher.
                    </Typography>
                    <Button
                        variant="contained"
                        startIcon={<RetryIcon />}
                        onClick={() => setRetryToken((t) => t + 1)}
                        sx={{
                            minHeight: 44,
                            backgroundColor: tutorTokens.accent,
                            '&:hover': { backgroundColor: tutorTokens.accentDark },
                            '&:active': { transform: 'scale(0.97)' },
                        }}
                    >
                        Try again
                    </Button>
                </Box>
            </Box>
        );
    }

    return (
        <Box sx={{ minHeight: '100vh', backgroundColor: '#f9fafb' }}>
            <ChatHeader
                onLogout={handleLogout}
                onClearChat={handleBackToChat}
                confirmDelete={false}
            />
            <Box sx={{ maxWidth: 1200, mx: 'auto', mt: 2 }}>
                <Box sx={{ px: 3, pt: 2 }}>
                    <Button
                        startIcon={<ArrowBackIcon />}
                        onClick={() => navigate('/chat')}
                        sx={{ textTransform: 'none', color: '#1976d2' }}
                    >
                        Back to Chat
                    </Button>
                </Box>
                <CurriculumPage
                    currentUser={currentUser}
                    currentUserRole={currentUser?.role}
                />
            </Box>
        </Box>
    );
};

export default StudentCurriculum;
